import { useEffect, useRef, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import useAxios from "../../utils/useAxios";

const SEARCH_DEBOUNCE_MS = 300;

const getTierOptionLabel = (option) => option?.tagNumber || option?.tierName || "";

// The filters as one string, so a new `filters` object with the same content
// each render doesn't restart the search.
const getFiltersKey = (filters) =>
  filters
    ? JSON.stringify([filters.kind ?? null, (filters.relatedTo ?? []).map(String)])
    : "";

const getResultsArray = (data) => {
  const normalized = Array.isArray(data) ? data : data?.results || [];
  return Array.isArray(normalized) ? normalized : [];
};

/**
 * Async, server-searched Tier picker - same shape as EquipmentAutocomplete,
 * for the "Related Tiers" field on Instrument forms and the "Connected JBPN"
 * field on Tier forms.
 *
 * @param {object} props
 * @param {string | number} props.plantId
 * @param {boolean} [props.multiple]
 * @param {object | object[] | null} [props.value]
 * @param {(value: object | object[] | null, reason: string) => void} props.onChange
 * @param {string} [props.label]
 * @param {number | string} [props.excludeId]
 * @param {boolean} [props.required]
 * @param {boolean} [props.error]
 * @param {string} [props.helperText]
 * @param {boolean} [props.disabled]
 * @param {() => void} [props.onBlur]
 * @param {{ kind?: string, relatedTo?: (string | number)[] }} [props.filters] -
 *   narrows the list: `kind: "jbpn"` to JBPNs, `relatedTo` to tiers under any
 *   of those equipment ids. Without it every tier of the plant is listed.
 * @param {string} [props.noOptionsText] - shown when nothing matches
 */
export default function TierAutocomplete({
  plantId,
  multiple = false,
  value,
  onChange,
  label = "Tier",
  excludeId = null,
  required = false,
  error = false,
  helperText = "",
  disabled = false,
  onBlur,
  filters = null,
  noOptionsText = "No matching tiers",
}) {
  const api = useAxios();
  const requestIdRef = useRef(0);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const filtersKey = getFiltersKey(filters);

  useEffect(() => {
    if (!open || !plantId) return undefined;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const controller = new AbortController();

    const searchTimer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const params = { plant: plantId, search: search.trim() || undefined, page_size: 20 };
        const config = { params, signal: controller.signal };
        if (filtersKey) {
          const [kind, relatedTo] = JSON.parse(filtersKey);
          if (kind) params.kind = kind;
          if (relatedTo.length > 0) {
            params.relatedTo = relatedTo;
            // The API reads repeated keys (relatedTo=1&relatedTo=2), not relatedTo[]=.
            config.paramsSerializer = { indexes: null };
          }
        }
        const response = await api.get("/tiers/", config);
        if (requestIdRef.current === requestId) {
          const items = getResultsArray(response.data);
          setOptions(
            excludeId != null ? items.filter((item) => item.id !== excludeId) : items,
          );
        }
      } catch (err) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        console.error("Error searching tiers:", err);
      } finally {
        if (requestIdRef.current === requestId) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(searchTimer);
      controller.abort();
    };
  }, [api, excludeId, filtersKey, open, plantId, search]);

  return (
    <Autocomplete
      multiple={multiple}
      disableCloseOnSelect={multiple}
      options={options}
      loading={loading}
      filterOptions={(opts) => opts} // server already filtered
      getOptionLabel={getTierOptionLabel}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      value={value ?? (multiple ? [] : null)}
      onChange={(_, selected, reason) => onChange(selected, reason)}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onInputChange={(_, next) => setSearch(next)}
      onBlur={onBlur}
      disabled={disabled}
      noOptionsText={plantId ? noOptionsText : "Choose a plant first"}
      // Gloved fingers: rows a comfortable tap high on touch screens.
      componentsProps={{
        paper: {
          sx: {
            "@media (pointer: coarse)": {
              // Named through the listbox to outrank MUI's own rule, which
              // lets rows shrink from the `sm` breakpoint up (a tablet).
              "& .MuiAutocomplete-listbox .MuiAutocomplete-option": { minHeight: 48 },
            },
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          fullWidth
          required={required}
          error={error}
          helperText={helperText}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
